import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import jwt_decode from "jwt-decode";

import { AlertModal } from "stories/components";
import { refreshSession } from "services/api";
import { endSession } from "services/session";
import { logError } from "services/logger";

// How long before the refresh token dies the warning appears.
const WARN_BEFORE_MS = 2 * 60 * 1000;

const readExpiryMs = () => {
  const refreshToken = localStorage.getItem("refreshToken");
  if (!refreshToken) {
    return null;
  }
  try {
    const { exp } = jwt_decode(refreshToken);
    return typeof exp === "number" ? exp * 1000 : null;
  } catch (e) {
    return null;
  }
};

const formatRemaining = (ms) => {
  const total = Math.max(0, Math.round(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}.${String(seconds).padStart(2, "0")}`;
};

/**
 * Warns before the session ends instead of just navigating away.
 *
 * The app used to jump straight to the login screen the moment the refresh token
 * expired, with no warning and no explanation, so a half-filled transaction form
 * was simply lost. This gives the user two minutes and a way to stay signed in.
 * (Sovellon #2260, batch D.)
 */
const SessionExpiryWarning = () => {
  const { t } = useTranslation();
  const [remaining, setRemaining] = useState(null);
  const [extending, setExtending] = useState(false);
  const warnTimer = useRef(null);
  const tick = useRef(null);

  const clearTimers = useCallback(() => {
    if (warnTimer.current) { clearTimeout(warnTimer.current); warnTimer.current = null; }
    if (tick.current) { clearInterval(tick.current); tick.current = null; }
  }, []);

  const schedule = useCallback(() => {
    clearTimers();
    setRemaining(null);

    const expiresAt = readExpiryMs();
    if (expiresAt === null) {
      return;
    }

    const msUntilWarning = expiresAt - WARN_BEFORE_MS - Date.now();
    const startCountdown = () => {
      setRemaining(Math.max(0, readExpiryMs() - Date.now()));
      tick.current = setInterval(() => {
        const left = readExpiryMs() - Date.now();
        if (left <= 0) {
          clearTimers();
          setRemaining(0);
          return;
        }
        setRemaining(left);
      }, 1000);
    };

    if (msUntilWarning <= 0) {
      startCountdown();
      return;
    }
    warnTimer.current = setTimeout(startCountdown, msUntilWarning);
  }, [clearTimers]);

  useEffect(() => {
    schedule();
    return clearTimers;
  }, [schedule, clearTimers]);

  const handleLogout = () => {
    clearTimers();
    endSession();
  };

  const handleExtend = () => {
    setExtending(true);
    refreshSession()
      .then(() => schedule())
      .catch((error) => {
        logError("Session extend failed", error);
        // The interceptor already ends the session when a refresh genuinely fails.
      })
      .finally(() => setExtending(false));
  };

  if (remaining === null) {
    return null;
  }

  return (
    <AlertModal
      title={t("session_expiry_title", "Istunto vanhenee pian")}
      confirmBtnText={extending
        ? t("session_expiry_extending", "Jatketaan...")
        : t("session_expiry_extend", "Jatka istuntoa")}
      cancelBtnText={t("session_expiry_logout", "Kirjaudu ulos")}
      showCancel
      onConfirm={handleExtend}
      onCancel={handleLogout}
    >
      <div className="text-center">
        <div style={{ fontSize: "2rem", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
          {formatRemaining(remaining)}
        </div>
        <div className="text-muted">
          {t("session_expiry_text", "Tallentamattomat muutokset menetetään.")}
        </div>
      </div>
    </AlertModal>
  );
};

export default SessionExpiryWarning;
